import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/shared/context/AuthContext';
import { buildRealtimeWsUrl } from './buildRealtimeWsUrl';
import { DedupeSeenEvents } from './dedupeSeenEvents';
import { handleRealtimeEvent } from './realtimeEventHandlers';
import { REALTIME_LIVE_STALE_MS } from './realtimePollFallback';
import type { RealtimeWireMessage } from './types';

const MIN_RECONNECT_MS = 1_000;
const MAX_RECONNECT_MS = 30_000;
const PING_INTERVAL_MS = 25_000;
const LIVE_CHECK_MS = 5_000;

export type RealtimeConnectionStatus = {
  connected: boolean;
  /** True only after a domain event, not TCP open or ping/pong. */
  live: boolean;
};

export function useRealtimeConnection(options?: { enabled?: boolean }): RealtimeConnectionStatus {
  const enabled = options?.enabled ?? true;
  const { token, isAuthenticated, user } = useAuth();
  const queryClient = useQueryClient();
  const [connected, setConnected] = useState(false);
  const [live, setLive] = useState(false);

  const wsRef = useRef<WebSocket | null>(null);
  const reconnectAttemptRef = useRef(0);
  const reconnectTimerRef = useRef<number | null>(null);
  const pingTimerRef = useRef<number | null>(null);
  const liveCheckTimerRef = useRef<number | null>(null);
  const lastInboundAtRef = useRef(0);
  const dedupeRef = useRef(new DedupeSeenEvents());
  const queryClientRef = useRef(queryClient);
  queryClientRef.current = queryClient;
  const viewerUserIdRef = useRef(user?.id);
  viewerUserIdRef.current = user?.id;

  useEffect(() => {
    const clearTimers = () => {
      if (reconnectTimerRef.current != null) {
        window.clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      if (pingTimerRef.current != null) {
        window.clearInterval(pingTimerRef.current);
        pingTimerRef.current = null;
      }
      if (liveCheckTimerRef.current != null) {
        window.clearInterval(liveCheckTimerRef.current);
        liveCheckTimerRef.current = null;
      }
    };

    const markConnected = () => {
      setConnected(true);
    };

    const markDomainEvent = () => {
      lastInboundAtRef.current = Date.now();
      setConnected(true);
      setLive(true);
    };

    const refreshLive = () => {
      if (!lastInboundAtRef.current) {
        setLive(false);
        return;
      }
      setLive(Date.now() - lastInboundAtRef.current < REALTIME_LIVE_STALE_MS);
    };

    const sendPing = (ws: WebSocket) => {
      if (ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ type: 'ping' }));
      }
    };

    const closeSocket = () => {
      clearTimers();
      const ws = wsRef.current;
      wsRef.current = null;
      if (ws) {
        ws.onopen = null;
        ws.onclose = null;
        ws.onerror = null;
        ws.onmessage = null;
        if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) {
          ws.close();
        }
      }
      lastInboundAtRef.current = 0;
      setConnected(false);
      setLive(false);
    };

    const scheduleReconnect = () => {
      if (!enabled || !token || !isAuthenticated) return;
      const attempt = reconnectAttemptRef.current;
      const delay = Math.min(MAX_RECONNECT_MS, MIN_RECONNECT_MS * 2 ** attempt);
      reconnectAttemptRef.current = attempt + 1;
      reconnectTimerRef.current = window.setTimeout(() => {
        reconnectTimerRef.current = null;
        openConnection();
      }, delay);
    };

    const openConnection = () => {
      if (!enabled || !token || !isAuthenticated) {
        closeSocket();
        return;
      }

      closeSocket();
      dedupeRef.current = new DedupeSeenEvents();

      const ws = new WebSocket(buildRealtimeWsUrl(token));
      wsRef.current = ws;

      ws.onopen = () => {
        reconnectAttemptRef.current = 0;
        setConnected(true);
        sendPing(ws);
        pingTimerRef.current = window.setInterval(() => sendPing(ws), PING_INTERVAL_MS);
        liveCheckTimerRef.current = window.setInterval(refreshLive, LIVE_CHECK_MS);
      };

      ws.onmessage = (evt) => {
        let parsed: RealtimeWireMessage;
        try {
          parsed = JSON.parse(String(evt.data)) as RealtimeWireMessage;
        } catch {
          return;
        }

        if (parsed.kind === 'connected' || parsed.kind === 'pong') {
          markConnected();
          return;
        }

        if (parsed.kind !== 'event' || !parsed.event?.id) return;
        markDomainEvent();
        if (dedupeRef.current.has(parsed.event.id)) return;
        dedupeRef.current.remember(parsed.event.id);
        handleRealtimeEvent(queryClientRef.current, parsed.event, {
          viewerUserId: viewerUserIdRef.current,
        });
      };

      ws.onclose = () => {
        setConnected(false);
        setLive(false);
        lastInboundAtRef.current = 0;
        clearTimers();
        wsRef.current = null;
        scheduleReconnect();
      };

      ws.onerror = () => {
        setConnected(false);
        setLive(false);
      };
    };

    openConnection();

    return () => {
      closeSocket();
    };
  }, [enabled, isAuthenticated, token]);

  return { connected, live };
}
